import { makeStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMobileLayout from 'Hooks/useMobileLayout';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import {
  useScenarioSelectionRedefinedWrapperTemplate,
} from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedWrapperTemplate/useScenarioSelectionRedefinedWrapperTemplate';
import {
  useGoalStrategiesDetailsContext,
} from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedTheYearAheadGoalStrategiesDetailsStep/Content/GoalStrategiesDetailsContext';
import React, { useLayoutEffect, useRef } from 'react';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { RightColumnProvider } from 'Context/RightColumnContext';
import { FDBodyText, FDBox, FDStack } from '@facet-wealth/design';
import ScenarioSelectionRedefinedWrapperHeader
  from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedWrapperTemplate/ScenarioSelectionRedefinedWrapperHeader';
import StepNavigator from 'Components/ComponentLibrary/Molecules/StepNavigator';
import {
  ScenarioSelectionRedefinedTheYearAheadGoalStrategiesDetailsStepLayout,
} from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedTheYearAheadGoalStrategiesDetailsStep/Layout';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import {
  removeQuery,
} from 'Helpers/Query';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import { scrollToRef } from 'Helpers/ScrollToRef';
import { ModuleProgressStepper } from 'Organisms/ModuleProgressStepper/ModuleProgressStepper';
import {
  ExecuteSelectLoadingPage,
} from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedCommon/ExecuteSelectLoadingPage/ExecuteSelectLoadingPage';
import Box from '../../../../../../../Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';

const useStyles = makeStyles(theme => ({
  stepNavigator: {
    position: 'sticky',
    bottom: 0,
  },
  // We need to account for the space of the fixed bottom nav
  mobileHeight: {
    height: `calc(100dvh - ${theme.heights.mobileBottomNavigationHeight}px)`,
    overflow: 'auto',
  },
}));


const metaOverrideSet = buildFacetDesignOverride()
  .nameOverride(['plannerInsightsBody'], {
    _component: () => FDBodyText,
    _customProps: { color: 'textSecondary' },
  }, overrideReadOnly)
  .nameOverride(['footer', 'disclaimerText'], {
    _component: () => FDBodyText,
    _customProps: { color: 'textDisabled', size: 'sm', align: 'center' },
  }, overrideReadOnly);

export const ScenarioSelectionRedefinedTheYearAheadGoalStrategiesDetailsStepTemplate = ({ resource }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isMobile = useMobileLayout();
  const topRef = useRef();
  useBoneBackgroundColor();

  const {
    handleNext,
    handlePrevious,
    loading,
    isPageContentLoading,
    isSelecting,
  } = useScenarioSelectionRedefinedWrapperTemplate({
    resource,
  });
  const {
    currentGoalIdx,
    handleSetGoal,
    isLastGoal,
  } = useGoalStrategiesDetailsContext();

  const hasProgressSteps = resource.attr('progressSteps')?.resources().length() > 0;

  const { displayErrorMessage } = useSnackbarContext();

  // When we change the goal index that we are looking at we should scroll the user to the top of the page
  useLayoutEffect(() => {
    scrollToRef(topRef, { behavior: 'smooth' });
  }, [currentGoalIdx]);


  if (resource.isTruthy('shouldExecuteSelect') && isSelecting) {
    return <ExecuteSelectLoadingPage resource={resource} />;
  }

  const onNext = async () => {
    try {
      // This wizard step contains a stepper that navigates through a collection of goals that all
      // load at once.
      // If we are at the end of the goals collection we need to submit the current wizard step.
      // otherwise we are still navigating between the goals in the collection and just need to update the index
      if (isLastGoal) {
        await handleNext();
        return;
      }

      handleSetGoal(currentGoalIdx + 1);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  const onBack = async () => {
    if (currentGoalIdx > 0) {
      handleSetGoal(currentGoalIdx - 1);
    } else {
      await handlePrevious();
      removeQuery('currentGoalId');
    }
  };

  const stepNavigatorLabel = (isLastGoal ? resource.attr('lastGoalPrimaryActionLabel') : resource.attr('primaryActionLabel')) || 'Continue';
  return (
    <RightColumnProvider>
      <Box className={{ [classes.mobileHeight]: isMobile }}>
        <FDStack direction={'column'} minHeight={'100dvh'} width={'100%'}>
          <FDBox ref={topRef} />
          <ScenarioSelectionRedefinedWrapperHeader resource={resource} />
          {hasProgressSteps ? <ModuleProgressStepper stepCollection={resource.attr('progressSteps')} /> : null }
          <MetaResourceRenderer
            resource={resource}
            layout={ScenarioSelectionRedefinedTheYearAheadGoalStrategiesDetailsStepLayout}
            metaOverrideSet={metaOverrideSet}
          />
          <Box className={classes.stepNavigator}>
            <StepNavigator
              onNext={onNext}
              onPrevious={onBack}
              isLoading={isPageContentLoading || loading}
            >
              {stepNavigatorLabel}
            </StepNavigator>
          </Box>
        </FDStack>
      </Box>
    </RightColumnProvider>
  );
};


ScenarioSelectionRedefinedTheYearAheadGoalStrategiesDetailsStepTemplate.propTypes = {
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};
